import { ConfirmDiscardDialog } from "@dashboard/components/common/ConfirmDiscardDialog";
import {
    DashboardCustomizationContext,
    type PendingDashboardCustomizationAction,
} from "@dashboard/contexts/dashboardCustomizationContext";
import { useBlocker } from "@tanstack/react-router";
import { useCallback, useRef, useState, type ReactNode } from "react";

export function DashboardCustomizationProvider({ children }: { children: ReactNode }) {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [pendingAction, setPendingAction] = useState<PendingDashboardCustomizationAction | null>(
        null,
    );
    const isConfirming = useRef(false);

    const shouldBlockNavigation = useCallback(() => hasUnsavedChanges, [hasUnsavedChanges]);
    const navigationBlocker = useBlocker({
        shouldBlockFn: shouldBlockNavigation,
        enableBeforeUnload: () => hasUnsavedChanges,
        withResolver: true,
    });

    const requestDiscardConfirmation = useCallback(
        (action: PendingDashboardCustomizationAction) => {
            if (hasUnsavedChanges) {
                setPendingAction(() => action);
                return;
            }

            void action();
        },
        [hasUnsavedChanges],
    );

    const handleConfirmDiscard = useCallback(() => {
        isConfirming.current = true;

        setHasUnsavedChanges(false);
        if (pendingAction) {
            setPendingAction(null);
            void pendingAction();
            return;
        }

        if (navigationBlocker.status === "blocked") {
            navigationBlocker.proceed();
        }
    }, [navigationBlocker, pendingAction]);

    const handleDiscardDialogOpenChange = useCallback(
        (open: boolean) => {
            if (open) return;

            if (isConfirming.current) {
                isConfirming.current = false;
                return;
            }

            setPendingAction(null);
            if (navigationBlocker.status === "blocked") {
                navigationBlocker.reset();
            }
        },
        [navigationBlocker],
    );

    return (
        <DashboardCustomizationContext.Provider
            value={{ setHasUnsavedChanges, requestDiscardConfirmation }}
        >
            {children}
            <ConfirmDiscardDialog
                open={pendingAction !== null || navigationBlocker.status === "blocked"}
                onOpenChange={handleDiscardDialogOpenChange}
                onConfirm={handleConfirmDiscard}
            />
        </DashboardCustomizationContext.Provider>
    );
}
